import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import {
  type PointerEvent,
  type ReactNode,
  type Ref,
  useEffect,
  useEffectEvent,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { cn } from "@/shared/lib";

import { drawMatchMap, type MapFrame, type MapImages } from "../../map/drawMatchMap";
import type { MapColors } from "../../map/MapColors";
import type { MapScene } from "../../map/MapScene";
import { clampView, fitView, type MapView, zoomAt } from "../../map/MapView";
import { WORLD_SIZE } from "../../map/WORLD_SIZE";
import { ZOOM_KEY } from "./zoomKey";

const LABEL = msg({
  id: "analysis.map.canvas",
  message: "Map of the match from above: where you were, deaths, camps and buildings",
});
const WHEEL_HINT = msg({ id: "analysis.map.wheel_hint", message: "{key} + wheel to zoom" });
const TOUCH_HINT = msg({ id: "analysis.map.touch_hint", message: "Move the map with two fingers" });

// Насколько можно приблизить карту относительно «вся карта».
const MAX_ZOOM = 6;
// Приближение колесом — по силе прокрутки: щелчок колеса мыши (100 px) — примерно ×1,16,
// мелкие шаги трекпада и щипка — плавно. За одно событие — не больше чем вдвое.
const WHEEL_SENSITIVITY = 0.0015;
const WHEEL_MAX_STEP = 2;
// Сколько висит подсказка про Ctrl и про два пальца, мс.
const HINT_MS = 1500;

// Карточка не выходит за край карты: отступ от краёв и место над точкой, px. На узкой
// карте (телефон) карточка над точкой закрыла бы полкарты и кнопки — она внизу карты.
const CARD_EDGE = 110;
const CARD_ABOVE = 90;
const DOCK_BELOW_PX = 480;

// Управление видом снаружи: кнопки «+», «−» и «вся карта».
export type MapCanvasHandle = {
  zoomBy: (factor: number) => void;
  fit: () => void;
};

type MapCanvasProps = {
  scene: MapScene;
  frame: MapFrame;
  images: MapImages;
  colors: MapColors;
  // Во весь экран страницы под картой нет: колесо приближает без Ctrl, один палец двигает.
  fullscreen: boolean;
  // Карточка события поверх карты — над местом `frame.highlight`.
  overlay: ReactNode;
  ref?: Ref<MapCanvasHandle>;
};

// Прокрутка колеса в пикселях: строки и страницы бывают у мыши в некоторых системах.
const wheelPixels = (event: WheelEvent, height: number): number =>
  event.deltaMode === 1
    ? event.deltaY * 16
    : event.deltaMode === 2
      ? event.deltaY * height
      : event.deltaY;

// Canvas тепловой карты: перерисовка при каждом изменении, сдвиг перетаскиванием,
// приближение Ctrl + колесом и щипком. Колесо без Ctrl листает страницу, а на телефоне один
// палец листает страницу, карту двигают двумя — карта не ловит прокрутку.
export const MapCanvas = ({
  scene,
  frame,
  images,
  colors,
  fullscreen,
  overlay,
  ref,
}: MapCanvasProps) => {
  const { _ } = useLingui();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  // Сдвиг и приближение пользователя — для текущего размера; сменился размер или нажата
  // «вся карта» — снова вся карта.
  const [custom, setCustom] = useState<{ key: string; view: MapView } | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const [hint, setHint] = useState<"wheel" | "touch" | null>(null);
  const hintTimer = useRef<number | undefined>(undefined);

  // Размер области: canvas занимает весь контейнер.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      if (entry !== undefined) {
        setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
      }
    });
    observer.observe(canvas);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(
    () => () => {
      window.clearTimeout(hintTimer.current);
    },
    [],
  );

  const key = `${size.width}x${size.height}`;
  const fitted = size.width > 0 && size.height > 0 ? fitView(size.width, size.height) : null;
  const view = custom?.key === key ? custom.view : fitted;
  const minScale = fitted?.scale ?? 0;

  // Изменить вид от текущего: функцией, чтобы частые события не теряли друг друга. Карта
  // не уходит за край области.
  const change = (update: (current: MapView) => MapView) => {
    if (fitted === null) {
      return;
    }
    setCustom((previous) => ({
      key,
      view: clampView(
        update(previous?.key === key ? previous.view : fitted),
        size.width,
        size.height,
      ),
    }));
  };
  const zoom = (factor: number, x: number, y: number) => {
    change((current) => zoomAt(current, factor, x, y, minScale, minScale * MAX_ZOOM));
  };
  const showHint = (kind: "wheel" | "touch") => {
    setHint(kind);
    window.clearTimeout(hintTimer.current);
    hintTimer.current = window.setTimeout(() => {
      setHint(null);
    }, HINT_MS);
  };

  useImperativeHandle(ref, () => ({
    zoomBy: (factor) => {
      zoom(factor, size.width / 2, size.height / 2);
    },
    fit: () => {
      setCustom(null);
    },
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d") ?? null;
    if (canvas === null || context === null || view === null) {
      return;
    }
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(size.width * dpr);
    canvas.height = Math.round(size.height * dpr);
    drawMatchMap(context, { ...size, dpr }, view, scene, frame, images, colors);
  }, [size, view, scene, frame, images, colors]);

  // Колесо и касания обрабатываем не через React: его обработчики пассивные и не могут
  // отменить прокрутку страницы.
  const onWheel = useEffectEvent((event: WheelEvent, canvas: HTMLCanvasElement) => {
    // Щипок на трекпаде приходит колесом с `ctrlKey`.
    if (!fullscreen && !event.ctrlKey && !event.metaKey) {
      showHint("wheel");
      return;
    }
    event.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const factor = Math.exp(-wheelPixels(event, rect.height) * WHEEL_SENSITIVITY);
    zoom(
      Math.max(1 / WHEEL_MAX_STEP, Math.min(WHEEL_MAX_STEP, factor)),
      event.clientX - rect.left,
      event.clientY - rect.top,
    );
  });
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) {
      return;
    }
    const wheel = (event: WheelEvent) => {
      onWheel(event, canvas);
    };
    // Два пальца — карта, а не прокрутка или приближение страницы.
    const touchMove = (event: TouchEvent) => {
      if (event.touches.length >= 2 && event.cancelable) {
        event.preventDefault();
      }
    };
    canvas.addEventListener("wheel", wheel, { passive: false });
    canvas.addEventListener("touchmove", touchMove, { passive: false });
    return () => {
      canvas.removeEventListener("wheel", wheel);
      canvas.removeEventListener("touchmove", touchMove);
    };
  }, []);

  const local = (event: PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };
  const release = (event: PointerEvent<HTMLCanvasElement>) => {
    pointers.current.delete(event.pointerId);
  };

  // Точка события на экране: у повёрнутой карты — от повёрнутой точки.
  const flipped = scene.team === 1;
  const anchor =
    frame.highlight === null || view === null
      ? null
      : {
          x: view.ox + view.scale * (flipped ? WORLD_SIZE - frame.highlight.x : frame.highlight.x),
          y: view.oy + view.scale * (flipped ? WORLD_SIZE - frame.highlight.y : frame.highlight.y),
        };

  return (
    <div className="relative size-full">
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={_(LABEL)}
        data-fullscreen={fullscreen}
        className={cn(
          "block size-full cursor-grab active:cursor-grabbing",
          fullscreen ? "touch-none" : "touch-pan-x touch-pan-y",
        )}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          pointers.current.set(event.pointerId, local(event));
        }}
        onPointerMove={(event) => {
          const previous = pointers.current.get(event.pointerId);
          if (previous === undefined) {
            return;
          }
          const point = local(event);
          const [other] = [...pointers.current].filter(([id]) => id !== event.pointerId);
          if (other === undefined) {
            // Мышь — сдвиг; палец — только во весь экран, иначе им листают страницу.
            if (event.pointerType !== "touch" || fullscreen) {
              change((current) => ({
                ...current,
                ox: current.ox + point.x - previous.x,
                oy: current.oy + point.y - previous.y,
              }));
            }
          } else {
            // Два пальца: середина между ними двигает карту, расстояние — приближает.
            const [, fixed] = other;
            const before = Math.hypot(previous.x - fixed.x, previous.y - fixed.y);
            const after = Math.hypot(point.x - fixed.x, point.y - fixed.y);
            change((current) =>
              zoomAt(
                {
                  ...current,
                  ox: current.ox + (point.x - previous.x) / 2,
                  oy: current.oy + (point.y - previous.y) / 2,
                },
                before > 0 ? after / before : 1,
                (point.x + fixed.x) / 2,
                (point.y + fixed.y) / 2,
                minScale,
                minScale * MAX_ZOOM,
              ),
            );
          }
          pointers.current.set(event.pointerId, point);
        }}
        onPointerUp={release}
        onPointerCancel={(event) => {
          // Браузер забрал палец под прокрутку страницы — подсказать про два пальца.
          if (event.pointerType === "touch" && !fullscreen && pointers.current.size === 1) {
            showHint("touch");
          }
          release(event);
        }}
      />
      {anchor !== null && overlay !== null && size.width < DOCK_BELOW_PX && (
        <div className="pointer-events-none absolute inset-x-2 bottom-2 flex justify-center">
          {overlay}
        </div>
      )}
      {anchor !== null && overlay !== null && size.width >= DOCK_BELOW_PX && (
        <div
          className="pointer-events-none absolute"
          style={{
            left: Math.max(CARD_EDGE, Math.min(size.width - CARD_EDGE, anchor.x)),
            top: anchor.y > CARD_ABOVE ? anchor.y - 44 : anchor.y + 44,
            transform: `translate(-50%, ${anchor.y > CARD_ABOVE ? "-100%" : "0"})`,
          }}
        >
          {overlay}
        </div>
      )}
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 flex items-center justify-center bg-background/55 transition-opacity",
          hint === "wheel" ? "opacity-100" : "opacity-0",
        )}
      >
        <span className="rounded-[10px] border border-border-strong bg-background/90 px-4 py-2.5 text-[15px] font-semibold">
          {_({ ...WHEEL_HINT, values: { key: ZOOM_KEY } })}
        </span>
      </div>
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-background/90 px-3 py-1 text-xs whitespace-nowrap text-foreground-soft transition-opacity",
          hint === "touch" ? "opacity-100" : "opacity-0",
        )}
      >
        {_(TOUCH_HINT)}
      </span>
    </div>
  );
};
